import React from 'react';
import { Modal } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileExplorerModalProps } from '../../../types/fileExplorerTypes';
import {
  checkFileLinks, CheckFileLinksResponse, deleteFile, TreeFile,
} from '../services';
import { ControlledAlertModal } from '../../common/ControlledModal';
import { useFileExplorerMutation } from '../../../hooks/useFileExplorerMutation';
import { Alert } from '../../common/Alert';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error al eliminar el archivo, por favor intenta nuevamente.';
const LINKED_FILE_WARNING = 'Estás eliminando un documento que está vinculado en secciones de la aplicación. Te recomendamos reemplazar el archivo en lugar de eliminarlo.';
const DEFAULT_DESCRIPTION = 'Esta acción no se puede deshacer. Esto eliminará permanentemente el archivo.';

function LinkedFilesExistsAlert(
  { linkData }:
  { linkData: CheckFileLinksResponse }) {
  return (
    <Alert variant="warning" className="mb-0" title={LINKED_FILE_WARNING}>
      {linkData?.links && linkData.links.length > 0 && (
        <ul className="mb-0 ps-3">
          {linkData.links.map((link) => (
            <li key={link.label} className="fs-base">
              {link.url ? (
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </a>
              ) : (
                link.label
              )}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

export function DeleteFileModal({ show, onHide, data }: FileExplorerModalProps<TreeFile>) {
  if (!data) return null;

  const { data: linkData, isLoading: isCheckingLinks } = useQuery({
    queryKey: ['file-links', data.id],
    queryFn: () => checkFileLinks(data.id),
    enabled: show,
  });

  const handleSuccess = () => {
    onHide?.();
  };

  const mutationFn = () => deleteFile(data.id);
  const {
    mutateAsync, isLoading, reset,
  } = useFileExplorerMutation({
    mutationFn,
    onSuccess: handleSuccess,
  });

  const handleDelete = (id: string) => {
    const deletePromise = mutateAsync(id);
    toast.promise(deletePromise, {
      loading: 'Eliminando archivo...',
      success: 'Archivo eliminado exitosamente',
      error: {
        message: 'Error al eliminar el archivo',
        description: GENERIC_ERROR_MESSAGE,
      },
    });
  };

  const handleClose = () => {
    reset();
    onHide?.();
  };

  const isLinked = linkData?.is_linked ?? false;

  return (
    <ControlledAlertModal
      show={show}
      onHide={handleClose}
      title="¿Está seguro/a?"
      description={DEFAULT_DESCRIPTION}
      closeButton
      centered
    >

      {isLinked && (
      <div className="px-4">
        <LinkedFilesExistsAlert linkData={linkData!} />
      </div>
      )}

      <Modal.Footer>
        <ControlledAlertModal.Actions
          handleClose={handleClose}
          onConfirm={() => handleDelete(data.id.toString())}
          isPending={isLoading || isCheckingLinks}
        />
      </Modal.Footer>

    </ControlledAlertModal>
  );
}
